import { useCallback, useMemo, useState, useEffect } from 'react';
import { Col, Form, Row } from 'antd';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import ErrorHelper from 'helpers/ErrorHelper';
import UIHelper from 'helpers/UIHelper';
import Footfalls from 'libs/api/admin/footfalls';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from 'hooks/useUser';
import moment from 'moment';
import { useApi } from 'hooks/useApi';

const api = new Footfalls();

const useFootfalls = (isMainPage = true, day, shoppingCentersId) => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const shoppingCenters = useRecoilValue(getShoppingCenters);

  const groupBy = searchParams.get('group_by');

  const [groupData, setGroupData] = useState(null);
  const [singleData, setSingleData] = useState([]);

  const {
    data,
    loading,
    setLoading,
    updateInProcess,
    setUpdateInProcess,
    handleDownloadExcel,
    onChangeTableFilter,
    onDelete: handleDeleteCategory,
  } = useApi({
    resourceKey: 'footfalls',
    singleResourceKey: 'footfall',
    api,
    isMainPage,
  });

  const filters = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: shoppingCenters.map((i) => ({
          value: i.id,
          title: i.title,
        })),
      },
      {
        title: t('Date range'),
        type: 'range',
        name: 'dateRange',
        keys: ['by_day_from', 'by_day_to'],
      },
      {
        title: t('Periodo'),
        type: 'select',
        name: 'group_by',
        values: [
          // { value: '', title: 'Raggruppamento per' },
          { value: 'day', title: t('Giorno') },
          { value: 'week', title: t('Settimana') },
          { value: 'month', title: t('Mese') },
          { value: 'year', title: t('Anno') },
        ],
      },
    ];
  }, [shoppingCenters, t]);

  const columns = useMemo(() => {
    return [
      {
        title: t('Data'),
        dataIndex: 'day',
        sorter: () => {},
        render: (row) => {
          //day
          // week
          // month
          // year
          switch (groupBy) {
            case 'day':
              return UIHelper.formatedDate(row, 'DD MMMM YYYY');
            case 'week':
              return `${t('Week')} ${moment(
                row,
              ).isoWeek()}, ${UIHelper.formatedDate(row, 'YYYY')}`;
            case 'month':
              return UIHelper.formatedDate(row, 'MMMM YYYY');
            case 'year':
              return UIHelper.formatedDate(row, 'YYYY');
            default:
              return `${t('Week')} ${moment(
                row,
              ).isoWeek()}, ${UIHelper.formatedDate(row, 'YYYY')}`;
          }
        },
      },
      {
        title: t('Gruppo'),
        dataIndex: 'shopping_center_id',
        sorter: () => {},
        render: (data, row) => row?.shopping_center?.title,
      },
      {
        title: t('Visitatori'),
        dataIndex: 'visitors',
        sorter: () => {},
      },
      {
        title: t('Azioni'),
        dataIndex: 'id',
        render: (id, row) => (
          <Link
            to={`${pathname}/${id}/edit?day=${row.day}&shopping_center_id=${row.shopping_center.id}`}
          >
            <Button>{t('Apri')}</Button>
          </Link>
        ),
      },
      // {
      //   title: t('Status'),
      //   dataIndex: 'status',
      //   render: (text, row) =>
      //     row.can_update !== false ? t('Active') : t('Inactive'),
      // },
      // {
      //   title: '',
      //   dataIndex: 'id',
      //   align: 'right',
      //   width: 120,
      //   render: (id, row) =>
      //     row.can_update !== false && (
      //       <Row gutter={16} justify={'end'}>
      //         <Col>
      //           <Link
      //             to={`${pathname}/${id}/edit?day=${row.day}&shopping_center_id=${row.shopping_center.id}`}
      //           >
      //             <Button type={'secondary'} icon={<EditIcon />} />
      //           </Link>
      //         </Col>
      //       </Row>
      //     ),
      // },
    ];
  }, [t, pathname, groupBy]);

  const columnsV2 = useMemo(() => {
    return [
      {
        title: 'Nome Gruppo',
        dataIndex: 'groupName',
      },
      {
        title: 'Entrate',
        dataIndex: 'groupEnters',
      },
      {
        title: 'Uscite',
        dataIndex: 'groupExits',
      },
    ];
  }, []);

  const tableData = useMemo(() => {
    return groupData && groupData.data
      ? groupData.data.map((groupItem) => ({
          key: groupItem.group.id,
          groupName: groupItem.group.name,
          groupEnters: groupItem.enters,
          groupExits: groupItem.exits,
        }))
      : [];
  }, [groupData]);

  const handleCreateOrUpdate = useCallback(
    async (values, day, shoppingCenterId) => {
      setUpdateInProcess(true);
      try {
        const dataPayload = {
          footfall: {
            ...values,
          },
        };
        if (!day) {
          await api.create({ data: dataPayload });
          navigate('/admin/footfalls');
        } else {
          const updateData = {
            footfalls: {
              ...values,
            },
          };
          await api.updateEdit(updateData, day, shoppingCenterId);
          navigate('/admin/footfalls');
        }
      } catch (e) {
        form.setFields(
          ErrorHelper.getErrorsForForm(e?.response?.data || {}, {
            shopping_center: 'shopping_center_id',
          }),
        );
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, setUpdateInProcess],
  );

  const handleImportExcel = useCallback(
    async (values) => {
      setUpdateInProcess(true);
      try {
        const formData = new FormData();
        for (let key in values) {
          if (key !== 'file') {
            formData.append(`${key}`, values[key] || '');
          } else {
            if (values[key]) {
              formData.append(`${key}`, values[key][0]?.originFileObj || '');
            }
          }
        }
        await api.importExcel(formData);
        navigate('/admin/footfalls');
      } catch (e) {
        const errors = e?.response?.data || [];
        if (errors.length) {
          form.setFields([
            {
              name: 'shopping_center_id',
              errors,
            },
          ]);
        } else {
          form.setFields([
            {
              name: 'file',
              errors: [t('Wrong File')],
            },
          ]);
        }
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, setUpdateInProcess, t],
  );

  useEffect(() => {
    (async () => {
      if (!isMainPage && id) {
        setLoading(true);
        try {
          const { data } = await api.getEdit(day, shoppingCentersId);
          if (Array.isArray(data?.footfalls)) {
            setSingleData((d) => [...d, ...data.footfalls]);
            form.setFields(
              data.footfalls.map((footfall) => ({
                name: footfall.id,
                value: footfall.visitors,
              })),
            );
          }
        } catch (e) {
          form.setFields(
            ErrorHelper.getErrorsForForm(e?.response?.data || {}, {
              shopping_center: 'shopping_center_id',
            }),
          );
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [day, isMainPage, shoppingCentersId, setLoading, form, id]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.getSingleItem(id);
        setGroupData(data);
      } catch (e) {
        console.log(e);
      }
    })();
  }, [id]);

  return {
    columns,
    columnsV2,
    tableData,
    data,
    singleData,
    groupData,
    updateInProcess,
    loading,
    onChangeTableFilter,
    form,
    filters,
    shoppingCenters,
    handleCreateOrUpdate,
    handleDeleteCategory,
    handleDownloadExcel,
    handleImportExcel,
  };
};

export default useFootfalls;
